import React from 'react'
import {Box, Typography, Tooltip} from '@mui/material'
import {FONT, WEIGHT} from '../../styles/foundation/typography'
import {BORDER, SUBTLE, NAVY, WHITE, PRIMARY_BLUE, CHART_PALETTE} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'

const TRACK = '#f1f4f8'

// Cockpit panel wrapper — bordered card with an uppercase ruled header. Replaces MUI Card/CardHeader.
export function Panel({title, sub, children, noPad}) {
  return (
    <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: SHADOW.card, overflow: 'hidden', height: '100%'}}>
      <Box sx={{px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`}}>
        <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.extra, letterSpacing: '0.07em', textTransform: 'uppercase', color: SUBTLE}}>{title}</Typography>
        {sub && <Typography sx={{fontFamily: FONT, fontSize: '10.5px', color: '#93a0b0', mt: 0.4, lineHeight: 1.4}}>{sub}</Typography>}
      </Box>
      <Box sx={{p: noPad ? 0 : 2}}>{children}</Box>
    </Box>
  )
}

// Vertical column chart. data: [{name, value, color?}].
export function VColumns({data = [], height = 180, color = PRIMARY_BLUE}) {
  const rows = (Array.isArray(data) ? data : []).map((d) => ({name: d.name ?? '—', value: Number(d.value) || 0, color: d.color}))
  const max = Math.max(...rows.map((r) => r.value), 1)
  if (rows.length === 0) return <Typography sx={{fontFamily: FONT, fontSize: 12, color: SUBTLE, py: 2, textAlign: 'center'}}>No data.</Typography>
  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'flex-end', gap: 1.5, height, borderBottom: `2px solid ${BORDER}`, pt: 1}}>
        {rows.map((r, i) => (
          <Box key={i} sx={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: 0.6}}>
            <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.extra, color: r.color || color}}>{r.value}</Typography>
            <Box sx={{width: '100%', flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center'}}>
              <Tooltip arrow title={`${r.name}: ${r.value.toLocaleString()}`}>
                <Box sx={{width: '62%', minHeight: 2, height: `${(r.value / max) * 100}%`, bgcolor: r.color || color, borderRadius: '4px 4px 0 0', cursor: 'default'}} />
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{display: 'flex', gap: 1.5, mt: 0.75}}>
        {rows.map((r, i) => (
          <Typography key={i} sx={{flex: 1, textAlign: 'center', fontFamily: FONT, fontSize: '9.5px', color: SUBTLE, lineHeight: 1.2}}>{r.name}</Typography>
        ))}
      </Box>
    </Box>
  )
}

// Area curve for an ORDERED distribution (e.g. weight buckets light→heavy, ageing days).
// Labelled X/Y axes with a Y scale + gridlines, value labels on each point, and a rich
// interactive tooltip (hover anywhere in a band) showing the axis point, count and share.
// data: [{name|label, value}] in display order. xLabel/yLabel name the axes; valueName is
// what a value counts (default "Shipments"). bars=true draws a column per bucket with the
// trend line on top — a more detailed combo that reads well at full width.
export function AreaCurve({data = [], height = 240, color = PRIMARY_BLUE, xLabel, yLabel, valueName = 'Shipments', bars = false}) {
  const [tip, setTip] = React.useState(null) // {name, value, pct}
  const [pos, setPos] = React.useState({x: 0, y: 0})
  const ref = React.useRef(null)
  const gid = 'ac' + React.useId().replace(/:/g, '') // unique gradient id per instance
  const rows = (Array.isArray(data) ? data : []).map((d) => ({name: d.name ?? d.label ?? '—', value: Number(d.value) || 0}))
  if (rows.length === 0) return <Typography sx={{fontFamily: FONT, fontSize: 12, color: SUBTLE, py: 2, textAlign: 'center'}}>No data.</Typography>
  const total = rows.reduce((s, r) => s + r.value, 0) || 1
  const W = 720, H = height, L = 46, R = 18, T = 22, B = xLabel ? 50 : 36, pw = W - L - R, ph = H - T - B, n = rows.length
  const max = Math.max(...rows.map((r) => r.value), 1)
  const slot = pw / n
  // bars → bucket-centred; line-only → spread endpoints to the axis edges.
  const X = (i) => (bars ? L + slot * i + slot / 2 : L + (n === 1 ? pw / 2 : i * (pw / (n - 1))))
  const Y = (v) => T + ph * (1 - v / max)
  const yTicks = [...new Set([0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f)))]
  const barW = Math.min(slot * 0.6, 60)
  const bandX = (i) => (bars ? L + slot * i : (n === 1 ? L : (X(Math.max(i - 1, 0)) + X(i)) / 2))
  const bandW = (i) => (bars ? slot : (i < n - 1 ? (X(i) + X(i + 1)) / 2 : W - R) - bandX(i))
  let line = '', area = `M${X(0)} ${T + ph} `
  rows.forEach((r, i) => { line += `${i ? 'L' : 'M'}${X(i)} ${Y(r.value)} `; area += `L${X(i)} ${Y(r.value)} ` })
  area += `L${X(n - 1)} ${T + ph} Z`
  const onMove = (e) => { const b = ref.current?.getBoundingClientRect(); if (b) setPos({x: e.clientX - b.left, y: e.clientY - b.top}) }
  return (
    <Box ref={ref} sx={{position: 'relative', width: '100%', overflowX: 'auto'}} onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width='100%' style={{display: 'block', minWidth: 520}} role='img' aria-label='Distribution chart'>
        <defs>
          <linearGradient id={gid} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0' stopColor={color} stopOpacity='0.3' />
            <stop offset='1' stopColor={color} stopOpacity='0' />
          </linearGradient>
        </defs>
        {/* Y gridlines + scale */}
        {yTicks.map((t, i) => {
          const yy = Y(t)
          return (
            <g key={i}>
              <line x1={L} y1={yy} x2={W - R} y2={yy} stroke={BORDER} strokeWidth={t === 0 ? 1 : 0.6} strokeDasharray={t === 0 ? '0' : '3 3'} />
              <text x={L - 7} y={yy + 3} textAnchor='end' fontSize='9' fill={SUBTLE} fontFamily={FONT}>{t.toLocaleString()}</text>
            </g>
          )
        })}
        {/* optional faint bars behind the area (combo mode) */}
        {bars && rows.map((r, i) => {
          const active = tip && tip.name === r.name
          return <rect key={'b' + i} x={X(i) - barW / 2} y={Y(r.value)} width={barW} height={Math.max((T + ph) - Y(r.value), 0)} rx='3' fill={color} opacity={active ? 0.28 : 0.12} />
        })}
        <path d={area} fill={`url(#${gid})`} />
        <path d={line} fill='none' stroke={color} strokeWidth='2.5' />
        {rows.map((r, i) => {
          const pct = Math.round((r.value / total) * 100)
          const active = tip && tip.name === r.name
          return (
            <g key={i}>
              <circle cx={X(i)} cy={Y(r.value)} r={active ? '5' : '3.5'} fill={color} stroke={WHITE} strokeWidth={bars ? 1.5 : 0} />
              {r.value > 0 && <text x={X(i)} y={Y(r.value) - 8} textAnchor='middle' fontSize='9.5' fontWeight='800' fill={NAVY} fontFamily={FONT}>{r.value.toLocaleString()}</text>}
              <text x={X(i)} y={H - B + 16} textAnchor='middle' fontSize='9' fill={SUBTLE} fontFamily={FONT}>{r.name}</text>
              {/* full-height hover band so the tooltip triggers anywhere in the column */}
              <rect x={bandX(i)} y={T} width={Math.max(bandW(i), 1)} height={ph} fill='transparent'
                style={{cursor: 'default'}} onMouseEnter={() => setTip({name: r.name, value: r.value, pct})} />
            </g>
          )
        })}
        {/* axis titles */}
        {yLabel && <text transform={`translate(12 ${T + ph / 2}) rotate(-90)`} textAnchor='middle' fontSize='10' fontWeight='700' fill={SUBTLE} fontFamily={FONT}>{yLabel}</text>}
        {xLabel && <text x={L + pw / 2} y={H - 6} textAnchor='middle' fontSize='10' fontWeight='700' fill={SUBTLE} fontFamily={FONT}>{xLabel}</text>}
      </svg>
      {tip && (
        <Box sx={{
          position: 'absolute', left: Math.min(pos.x + 12, (ref.current?.clientWidth || 999) - 170), top: Math.max(pos.y - 14, 0),
          pointerEvents: 'none', bgcolor: NAVY, color: WHITE, px: 1.25, py: 0.75, borderRadius: '8px',
          boxShadow: '0 6px 18px rgba(15,27,45,0.28)', zIndex: 5, whiteSpace: 'nowrap',
        }}>
          <Typography sx={{fontFamily: FONT, fontSize: '10px', opacity: 0.75}}>{xLabel ? `${xLabel}: ` : ''}{tip.name}</Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25}}>
            <Box sx={{width: 9, height: 9, borderRadius: '2px', bgcolor: color}} />
            <Typography sx={{fontFamily: FONT, fontSize: '12px', fontWeight: WEIGHT.bold}}>{valueName}: {tip.value.toLocaleString()}</Typography>
          </Box>
          <Typography sx={{fontFamily: FONT, fontSize: '10.5px', opacity: 0.8, mt: 0.15}}>{tip.pct}% of {total.toLocaleString()}</Typography>
        </Box>
      )}
    </Box>
  )
}

// Thick horizontal bar chart. data: [{name, value, color?}].
export function BarsH({data = [], color = PRIMARY_BLUE, labelWidth = 104}) {
  const rows = (Array.isArray(data) ? data : []).map((d) => ({name: d.name ?? d.label ?? '—', value: Number(d.value) || 0, color: d.color}))
  const max = Math.max(...rows.map((r) => r.value), 1)
  if (rows.length === 0) return <Typography sx={{fontFamily: FONT, fontSize: 12, color: SUBTLE, py: 2, textAlign: 'center'}}>No data.</Typography>
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.4}}>
      {rows.map((r, i) => (
        <Box key={i} sx={{display: 'grid', gridTemplateColumns: `${labelWidth}px 1fr 52px`, gap: 1.2, alignItems: 'center'}}>
          <Typography sx={{fontFamily: FONT, fontSize: '11px', color: SUBTLE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{r.name}</Typography>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <Tooltip arrow title={`${r.name}: ${r.value.toLocaleString()}`}>
              <Box sx={{height: 20, minWidth: 3, width: `${(r.value / max) * 100}%`, bgcolor: r.color || color, borderRadius: '4px', cursor: 'default'}} />
            </Tooltip>
          </Box>
          <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.extra, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>{r.value.toLocaleString()}</Typography>
        </Box>
      ))}
    </Box>
  )
}

// Lollipop chart — ranked items as a faint stem + a value dot. Lighter ink than a full
// bar, so a long courier list reads as a clean ranking rather than a wall of colour.
// data: [{name, value, color?}] (sorted by caller). Labelled columns (nameLabel/valueLabel)
// mark what each axis is; hover shows the exact count, share of total and rank.
export function Lollipop({data = [], color = PRIMARY_BLUE, labelWidth = 116, nameLabel, valueLabel = 'Shipments'}) {
  const rows = (Array.isArray(data) ? data : []).map((d) => ({name: d.name ?? d.label ?? '—', value: Number(d.value) || 0, color: d.color}))
  const max = Math.max(...rows.map((r) => r.value), 1)
  const total = rows.reduce((s, r) => s + r.value, 0) || 1
  if (rows.length === 0) return <Typography sx={{fontFamily: FONT, fontSize: 12, color: SUBTLE, py: 2, textAlign: 'center'}}>No data.</Typography>
  const headSx = {fontFamily: FONT, fontSize: '9.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#93a0b0'}
  const grid = `${labelWidth}px 1fr 52px`
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.4}}>
      {/* column headers — mark what the axes are */}
      <Box sx={{display: 'grid', gridTemplateColumns: grid, gap: 1.2, alignItems: 'center', pb: 0.75, borderBottom: `1px solid ${BORDER}`}}>
        <Typography sx={headSx}>{nameLabel || 'Courier'}</Typography>
        <Typography sx={{...headSx, textAlign: 'left'}}>Ranked by {valueLabel.toLowerCase()} · longest first</Typography>
        <Typography sx={{...headSx, textAlign: 'right'}}>{valueLabel}</Typography>
      </Box>
      {rows.map((r, i) => {
        const pct = Math.max((r.value / max) * 100, 1)
        const share = Math.round((r.value / total) * 100)
        return (
          <Box key={i} sx={{display: 'grid', gridTemplateColumns: grid, gap: 1.2, alignItems: 'center'}}>
            <Typography sx={{fontFamily: FONT, fontSize: '11px', color: SUBTLE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{r.name}</Typography>
            <Tooltip arrow title={
              <Box sx={{py: 0.25}}>
                <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.bold}}>{r.name}</Typography>
                <Typography sx={{fontFamily: FONT, fontSize: '11px'}}>{valueLabel}: {r.value.toLocaleString()} ({share}% of {total.toLocaleString()})</Typography>
                <Typography sx={{fontFamily: FONT, fontSize: '10.5px', opacity: 0.8}}>Rank #{i + 1} of {rows.length}</Typography>
              </Box>
            }>
              <Box sx={{position: 'relative', height: 14, display: 'flex', alignItems: 'center', cursor: 'default'}}>
                <Box sx={{position: 'absolute', left: 0, right: 0, height: 2, bgcolor: TRACK, borderRadius: 2}} />
                <Box sx={{position: 'absolute', left: 0, width: `${pct}%`, height: 3, bgcolor: r.color || color, borderRadius: 2, opacity: 0.55}} />
                <Box sx={{position: 'absolute', left: `${pct}%`, transform: 'translateX(-50%)', width: 12, height: 12, borderRadius: '50%', bgcolor: r.color || color}} />
              </Box>
            </Tooltip>
            <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.extra, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>{r.value.toLocaleString()}</Typography>
          </Box>
        )
      })}
    </Box>
  )
}

// Donut with center label + legend. data: [{name, value, color}].
export function Donut({data = [], centerValue, centerLabel, size = 130}) {
  const rows = (Array.isArray(data) ? data : []).filter((d) => Number(d.value) > 0)
  const total = rows.reduce((s, d) => s + Number(d.value), 0) || 1
  let acc = 0
  const stops = rows.map((d) => {
    const from = acc
    acc += (Number(d.value) / total) * 100
    return `${d.color} ${from.toFixed(2)}% ${acc.toFixed(2)}%`
  }).join(', ')
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, justifyContent: 'center', flexWrap: 'wrap', py: 1}}>
      <Box sx={{
        width: size, height: size, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', position: 'relative',
        background: `conic-gradient(${stops || `${BORDER} 0 100%`})`,
        '&::after': {content: '""', position: 'absolute', width: size * 0.62, height: size * 0.62, bgcolor: WHITE, borderRadius: '50%'},
      }}>
        <Box sx={{position: 'relative', zIndex: 1, textAlign: 'center'}}>
          <Typography sx={{fontFamily: FONT, fontSize: '20px', fontWeight: WEIGHT.extra, lineHeight: 1, color: rows[0]?.color || NAVY}}>{centerValue}</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: '9px', color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.05em'}}>{centerLabel}</Typography>
        </Box>
      </Box>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
        {rows.map((d, i) => {
          const pct = Math.round((Number(d.value) / total) * 100)
          return (
            <Tooltip key={i} arrow title={`${d.name}: ${Number(d.value).toLocaleString()} (${pct}% of ${total.toLocaleString()})`}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'default'}}>
                <Box sx={{width: 10, height: 10, borderRadius: '3px', bgcolor: d.color}} />
                <Typography sx={{fontFamily: FONT, fontSize: '12px', color: SUBTLE}}>{d.name} · <Box component='span' sx={{color: NAVY, fontWeight: WEIGHT.bold}}>{Number(d.value).toLocaleString()}</Box> <Box component='span' sx={{color: SUBTLE}}>({pct}%)</Box></Typography>
              </Box>
            </Tooltip>
          )
        })}
      </Box>
    </Box>
  )
}

// Per-row stacked composition bars. rows: [{name, values:{<key>:count}}]; buckets: [{key,label,color}].
// absolute=false → every bar fills the track (shows the mix). absolute=true → bar length is
// proportional to the row total (so rows can be ranked by size), segments still split by bucket.
export function StackedComposition({rows = [], buckets = [], absolute = false}) {
  const totalOf = (r) => buckets.reduce((s, b) => s + (Number(r.values?.[b.key]) || 0), 0)
  const globalMax = Math.max(...rows.map(totalOf), 1)
  return (
    <Box>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.4}}>
        {rows.map((r, i) => {
          const total = totalOf(r) || 1
          return (
            <Box key={i} sx={{display: 'grid', gridTemplateColumns: '110px 1fr 44px', gap: 1.2, alignItems: 'center'}}>
              <Typography sx={{fontFamily: FONT, fontSize: '11px', color: SUBTLE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{r.name}</Typography>
              <Box sx={{height: 18, borderRadius: '4px', overflow: 'hidden', bgcolor: TRACK}}>
                <Box sx={{display: 'flex', height: '100%', width: absolute ? `${(total / globalMax) * 100}%` : '100%'}}>
                  {buckets.map((b) => {
                    const v = Number(r.values?.[b.key]) || 0
                    if (v <= 0) return null
                    const share = Math.round((v / total) * 100)
                    return (
                      <Tooltip key={b.key} arrow title={`${r.name} · ${b.label}: ${v.toLocaleString()} (${share}% of ${total.toLocaleString()})`}>
                        <Box sx={{width: `${(v / total) * 100}%`, bgcolor: b.color, cursor: 'default'}} />
                      </Tooltip>
                    )
                  })}
                </Box>
              </Box>
              <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.extra, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>{total.toLocaleString()}</Typography>
            </Box>
          )
        })}
      </Box>
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: '6px 14px', mt: 1.75}}>
        {buckets.map((b) => (
          <Box key={b.key} sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Box sx={{width: 10, height: 10, borderRadius: '3px', bgcolor: b.color}} />
            <Typography sx={{fontFamily: FONT, fontSize: '11px', color: SUBTLE}}>{b.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

/**
 * New-UI chart primitives — hand-built SVG, matching the Data Cockpit mockup exactly.
 * These replace the legacy chart components (StackedBarChart / BarChart / OrderDistributionMap)
 * so every chart on the dashboard is drawn in the new design language.
 */

// Stacked column chart. data: [{name, <dataKey>: value, ...}]; series: [{dataKey, label, fill}].
export function StackedColumns({data = [], series = [], height = 280}) {
  const W = 900, H = height, L = 42, R = 14, T = 14, B = 30
  const pw = W - L - R, ph = H - T - B
  const totals = data.map((r) => series.reduce((s, se) => s + (Number(r[se.dataKey]) || 0), 0))
  const max = Math.max(...totals, 1)
  const n = Math.max(data.length, 1)
  const slot = pw / n
  const barW = Math.min(58, slot * 0.62)
  const yOf = (v) => T + ph * (1 - v / max)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f))

  return (
    <Box sx={{width: '100%', overflowX: 'auto'}}>
      <svg viewBox={`0 0 ${W} ${H}`} width='100%' style={{display: 'block', minWidth: 520}} role='img' aria-label='Stacked column chart'>
        {ticks.map((t, i) => {
          const yy = yOf(t)
          return (
            <g key={i}>
              <line x1={L} y1={yy} x2={W - R} y2={yy} stroke={BORDER} strokeWidth={i === 0 ? 1 : 0.6} strokeDasharray={i === 0 ? '0' : '3 3'} />
              <text x={L - 6} y={yy + 3} textAnchor='end' fontSize='9.5' fill={SUBTLE} fontFamily={FONT}>{t}</text>
            </g>
          )
        })}
        {data.map((row, i) => {
          let acc = 0
          const cx = L + slot * i + slot / 2
          return (
            <g key={i}>
              {series.map((se, si) => {
                const v = Number(row[se.dataKey]) || 0
                if (v <= 0) return null
                const h = ph * (v / max)
                acc += v
                return <rect key={si} x={cx - barW / 2} y={yOf(acc)} width={barW} height={h} fill={se.fill || CHART_PALETTE[si % CHART_PALETTE.length]} />
              })}
              <text x={cx} y={H - B + 16} textAnchor='middle' fontSize='9.5' fill={SUBTLE} fontFamily={FONT}>{row.name}</text>
            </g>
          )
        })}
      </svg>
    </Box>
  )
}

// Grouped column chart with a hover tooltip. data: [{name, <dataKey>: value}]; series: [{dataKey, label, fill}].
// Designed to be self-explanatory: axis + labels always visible, and hovering any bar shows
// exactly what it is (status · day · count) so a first-time user never has to guess.
export function GroupedColumns({data = [], series = [], height = 300}) {
  const [tip, setTip] = React.useState(null) // {name, label, value, color}
  const [pos, setPos] = React.useState({x: 0, y: 0})
  const ref = React.useRef(null)
  const W = 900, H = height, L = 44, R = 16, T = 18, B = 34
  const pw = W - L - R, ph = H - T - B
  const max = Math.max(...data.flatMap((row) => series.map((s) => Number(row[s.dataKey]) || 0)), 1)
  const n = Math.max(data.length, 1)
  const slot = pw / n
  const groupW = slot * 0.72
  const bw = Math.max(groupW / Math.max(series.length, 1), 3)
  const yOf = (v) => T + ph * (1 - v / max)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f))

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect()
    if (r) setPos({x: e.clientX - r.left, y: e.clientY - r.top})
  }

  return (
    <Box ref={ref} sx={{position: 'relative', width: '100%', overflowX: 'auto'}} onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width='100%' style={{display: 'block', minWidth: Math.max(520, n * 58)}} role='img' aria-label='Grouped column chart'>
        {ticks.map((t, i) => {
          const yy = yOf(t)
          return (
            <g key={i}>
              <line x1={L} y1={yy} x2={W - R} y2={yy} stroke={BORDER} strokeWidth={i === 0 ? 1 : 0.6} strokeDasharray={i === 0 ? '0' : '3 3'} />
              <text x={L - 6} y={yy + 3} textAnchor='end' fontSize='9.5' fill={SUBTLE} fontFamily={FONT}>{t}</text>
            </g>
          )
        })}
        {data.map((row, i) => {
          const gx = L + slot * i + (slot - groupW) / 2
          return (
            <g key={i}>
              {series.map((s, si) => {
                const v = Number(row[s.dataKey]) || 0
                const h = ph * (v / max)
                const fill = s.fill || CHART_PALETTE[si % CHART_PALETTE.length]
                return (
                  <rect key={si} x={gx + si * bw} y={yOf(v)} width={Math.max(bw - 2, 2)} height={h} rx='2' fill={fill}
                    style={{cursor: 'pointer'}}
                    onMouseEnter={() => setTip({name: row.name, label: s.label, value: v, color: fill})} />
                )
              })}
              <text x={gx + groupW / 2} y={H - B + 16} textAnchor='middle' fontSize='9.5' fill={SUBTLE} fontFamily={FONT}>{row.name}</text>
            </g>
          )
        })}
      </svg>
      {tip && (
        <Box sx={{
          position: 'absolute', left: Math.min(pos.x + 12, (ref.current?.clientWidth || 999) - 150), top: Math.max(pos.y - 12, 0),
          pointerEvents: 'none', bgcolor: NAVY, color: WHITE, px: 1.25, py: 0.75, borderRadius: '8px',
          boxShadow: '0 6px 18px rgba(15,27,45,0.28)', zIndex: 5, whiteSpace: 'nowrap',
        }}>
          <Typography sx={{fontFamily: FONT, fontSize: '10px', opacity: 0.75}}>{tip.name}</Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25}}>
            <Box sx={{width: 9, height: 9, borderRadius: '2px', bgcolor: tip.color}} />
            <Typography sx={{fontFamily: FONT, fontSize: '12px', fontWeight: WEIGHT.bold}}>{tip.label}: {tip.value.toLocaleString()} shipments</Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}

// Bubble timeline — each band is a bubble on a line, sized by value, coloured by speed.
// Self-explanatory + hover tooltip with the exact figure. data: [{name, value, color}].
export function BubbleTimeline({data = [], suffix = ''}) {
  const [tip, setTip] = React.useState(null)
  const [pos, setPos] = React.useState({x: 0, y: 0})
  const ref = React.useRef(null)
  const rows = (Array.isArray(data) ? data : []).map((d) => ({name: d.name ?? '—', value: Number(d.value) || 0, color: d.color || PRIMARY_BLUE}))
  const max = Math.max(...rows.map((r) => r.value), 1)
  const W = 760, H = 250, L = 40, R = 30, T = 30, B = 42, pw = W - L - R, ph = H - T - B, baseY = T + ph * 0.55
  const n = Math.max(rows.length, 1)
  const X = (i) => L + (n === 1 ? pw / 2 : i * (pw / (n - 1)))
  const rad = (v) => 12 + Math.sqrt(v / max) * 34
  const onMove = (e) => { const b = ref.current?.getBoundingClientRect(); if (b) setPos({x: e.clientX - b.left, y: e.clientY - b.top}) }
  return (
    <Box ref={ref} sx={{position: 'relative', width: '100%', overflowX: 'auto'}} onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width='100%' style={{display: 'block', minWidth: 520}} role='img' aria-label='Bubble timeline'>
        <line x1={L} y1={baseY} x2={W - R} y2={baseY} stroke={BORDER} strokeWidth='2' />
        {rows.map((r, i) => (
          <g key={i} style={{cursor: 'pointer'}} onMouseEnter={() => setTip(r)}>
            <circle cx={X(i)} cy={baseY} r={rad(r.value)} fill={r.color} opacity='0.55' stroke={r.color} strokeWidth='1.5' />
            <text x={X(i)} y={baseY + 4} textAnchor='middle' fontSize='11' fontWeight='800' fill={NAVY} fontFamily={FONT}>{r.value}{suffix}</text>
            <text x={X(i)} y={H - B + 18} textAnchor='middle' fontSize='9.5' fill={SUBTLE} fontFamily={FONT}>{r.name}</text>
          </g>
        ))}
      </svg>
      {tip && (
        <Box sx={{
          position: 'absolute', left: Math.min(pos.x + 12, (ref.current?.clientWidth || 999) - 150), top: Math.max(pos.y - 14, 0),
          pointerEvents: 'none', bgcolor: NAVY, color: WHITE, px: 1.25, py: 0.75, borderRadius: '8px',
          boxShadow: '0 6px 18px rgba(15,27,45,0.28)', zIndex: 5, whiteSpace: 'nowrap',
        }}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Box sx={{width: 9, height: 9, borderRadius: '2px', bgcolor: tip.color}} />
            <Typography sx={{fontFamily: FONT, fontSize: '12px', fontWeight: WEIGHT.bold}}>{tip.name}: {tip.value.toLocaleString()}{suffix} {suffix === '%' ? 'of shipments' : 'shipments'}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}

// Dot swarm — each dot ≈ N shipments; taller cluster = more shipments in that band.
// Dots are coloured by status so the whole multi-status breakdown stays visible.
// data: [{name, count, value, segments:[{label, count, color}]}]; legend: [{label, color}].
export function DotSwarm({data = [], legend = [], suffix = ''}) {
  const rows = Array.isArray(data) ? data : []
  const W = 760, H = 300, L = 40, R = 30, T = 24, B = 42, pw = W - L - R, baseY = H - B
  const n = Math.max(rows.length, 1)
  const X = (i) => L + (n === 1 ? pw / 2 : i * (pw / (n - 1)))
  const maxCount = Math.max(...rows.map((r) => Number(r.count) || 0), 1)
  const rowsAvail = Math.floor((baseY - (T + 16)) / 10)
  const per = Math.max(1, Math.ceil(maxCount / Math.max(rowsAvail * 3, 1)))

  return (
    <Box sx={{width: '100%', overflowX: 'auto'}}>
      <svg viewBox={`0 0 ${W} ${H}`} width='100%' style={{display: 'block', minWidth: 520}} role='img' aria-label='Dot swarm by status'>
        <line x1={L} y1={baseY} x2={W - R} y2={baseY} stroke={BORDER} />
        {rows.map((r, i) => {
          const cx = X(i)
          const segs = Array.isArray(r.segments) && r.segments.length ? r.segments : [{count: r.count, color: PRIMARY_BLUE}]
          const dots = []
          let j = 0
          segs.forEach((seg, si) => {
            const c = Number(seg.count) || 0
            const k = c > 0 ? Math.max(1, Math.round(c / per)) : 0
            for (let m = 0; m < k; m++) {
              const col = j % 3, row = Math.floor(j / 3)
              const y = baseY - 6 - row * 10
              if (y < T + 14) return
              dots.push(<circle key={si + '-' + m} cx={cx + (col - 1) * 11} cy={y} r='4.2' fill={seg.color || PRIMARY_BLUE} opacity='0.85' />)
              j++
            }
          })
          const topY = baseY - 6 - Math.min(Math.floor(Math.max(j - 1, 0) / 3), rowsAvail) * 10 - 12
          return (
            <g key={i}>
              <title>{`${r.name}: ${(Number(r.value) || 0).toLocaleString()}${suffix}${suffix === '%' ? ' of shipments' : ' shipments'}`}</title>
              {dots}
              {(Number(r.count) || 0) > 0 && <text x={cx} y={topY} textAnchor='middle' fontSize='10' fontWeight='800' fill={NAVY} fontFamily={FONT}>{r.value}{suffix}</text>}
              <text x={cx} y={H - B + 18} textAnchor='middle' fontSize='9.5' fill={SUBTLE} fontFamily={FONT}>{r.name}</text>
            </g>
          )
        })}
      </svg>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px 14px', mt: 1}}>
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: '6px 14px'}}>
          {legend.map((l) => (
            <Box key={l.label} sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
              <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: l.color}} />
              <Typography sx={{fontFamily: FONT, fontSize: '11px', color: SUBTLE}}>{l.label}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{fontFamily: FONT, fontSize: '10.5px', color: SUBTLE}}>Each dot ≈ {per} shipment{per > 1 ? 's' : ''}</Typography>
      </Box>
    </Box>
  )
}

// Status breakdown bars — colour · name · count · %, sorted. Self-explanatory even when
// the underlying distribution collapses to a single band. data: [{label, count, color, pct}].
export function StatusBars({data = [], note, shareMode = false}) {
  const rows = (Array.isArray(data) ? data : []).filter((r) => (Number(r.count) || 0) > 0).sort((a, b) => b.count - a.count)
  const max = Math.max(...rows.map((r) => Number(r.count) || 0), 1)
  if (rows.length === 0) {
    return <Typography sx={{fontFamily: FONT, fontSize: 12, color: SUBTLE, py: 3, textAlign: 'center'}}>No shipments in this range.</Typography>
  }
  return (
    <Box>
      {note && <Typography sx={{fontFamily: FONT, fontSize: '12px', color: SUBTLE, mb: 2}}>{note}</Typography>}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.6}}>
        {rows.map((r) => {
          const pct = r.pct != null ? r.pct : Math.round((r.count / max) * 100)
          return (
            <Box key={r.label} sx={{display: 'grid', gridTemplateColumns: {xs: '112px 1fr 92px', sm: '150px 1fr 108px'}, gap: 1.5, alignItems: 'center'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
                <Box sx={{width: 11, height: 11, borderRadius: '50%', bgcolor: r.color, flex: 'none'}} />
                <Typography sx={{fontFamily: FONT, fontSize: '12px', color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{r.label}</Typography>
              </Box>
              <Tooltip arrow title={`${r.label}: ${r.count.toLocaleString()} shipment${r.count === 1 ? '' : 's'} · ${pct}% of total`}>
                <Box sx={{height: 20, borderRadius: '5px', bgcolor: r.color, width: `${Math.max((r.count / max) * 100, 2)}%`, minWidth: 4, cursor: 'default'}} />
              </Tooltip>
              <Typography sx={{fontFamily: FONT, fontSize: '12px', color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                {shareMode
                  ? <><b>{pct}%</b> <Box component='span' sx={{color: SUBTLE}}>· {r.count.toLocaleString()}</Box></>
                  : <><b>{r.count.toLocaleString()}</b> <Box component='span' sx={{color: SUBTLE}}>· {pct}%</Box></>}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

// Simple histogram (ordered bins). data: [{label, value}].
export function Histogram({data = [], height = 260, color = PRIMARY_BLUE}) {
  const W = 600, H = height, L = 12, R = 12, T = 12, B = 26
  const pw = W - L - R, ph = H - T - B
  const max = Math.max(...data.map((d) => Number(d.value) || 0), 1)
  const n = Math.max(data.length, 1)
  const slot = pw / n
  const barW = slot * 0.6

  return (
    <Box sx={{width: '100%', overflowX: 'auto'}}>
      <svg viewBox={`0 0 ${W} ${H}`} width='100%' style={{display: 'block', minWidth: 420}} role='img' aria-label='Histogram'>
        <line x1={L} y1={T + ph} x2={W - R} y2={T + ph} stroke={BORDER} />
        {data.map((d, i) => {
          const v = Number(d.value) || 0
          const h = ph * (v / max)
          const x = L + slot * i + (slot - barW) / 2
          return (
            <g key={i} style={{cursor: 'default'}}>
              <title>{`${d.label}: ${v.toLocaleString()}`}</title>
              <rect x={x} y={T + ph - h} width={barW} height={h} rx='3' fill={color} />
              {v > 0 && <text x={x + barW / 2} y={T + ph - h - 5} textAnchor='middle' fontSize='9' fontWeight='700' fill={NAVY} fontFamily={FONT}>{v.toLocaleString()}</text>}
              <text x={x + barW / 2} y={T + ph + 16} textAnchor='middle' fontSize='8.5' fill={SUBTLE} fontFamily={FONT}>{d.label}</text>
            </g>
          )
        })}
      </svg>
    </Box>
  )
}

// Horizontal ranked bars — label · proportional bar · value. Replaces the legacy
// VerticalBarChart (category on the Y axis). data: [{name|label, value}].
export function HorizontalBars({data = [], color = PRIMARY_BLUE, labelWidth = 120}) {
  const rows = (Array.isArray(data) ? data : []).map((d) => ({name: d.name ?? d.label ?? '—', value: Number(d.value) || 0}))
  const max = Math.max(...rows.map((r) => r.value), 1)
  if (rows.length === 0) {
    return <Typography sx={{fontFamily: FONT, fontSize: 12, color: SUBTLE, py: 2, textAlign: 'center'}}>No data for this range.</Typography>
  }
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
      {rows.map((r, i) => (
        <Box key={i} sx={{display: 'grid', gridTemplateColumns: `${labelWidth}px 1fr 52px`, gap: 1, alignItems: 'center'}}>
          <Typography sx={{fontFamily: FONT, fontSize: '11.5px', color: SUBTLE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{r.name}</Typography>
          <Box sx={{height: 9, bgcolor: TRACK, borderRadius: '5px', overflow: 'hidden'}}>
            <Box sx={{height: '100%', width: `${(r.value / max) * 100}%`, bgcolor: color, borderRadius: '5px'}} />
          </Box>
          <Typography sx={{fontFamily: FONT, fontSize: '11.5px', fontWeight: WEIGHT.bold, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>{r.value.toLocaleString()}</Typography>
        </Box>
      ))}
    </Box>
  )
}

// Compact India map for the Delivery Spread panel. Clearly labelled ("India" + a legend)
// so a first-time user instantly reads it as "orders across India, bigger dot = more".
export function MiniMap() {
  return (
    <Box>
      <Box sx={{position: 'relative', aspectRatio: '1', borderRadius: '8px', border: `1px solid ${BORDER}`, bgcolor: '#eef4fc', overflow: 'hidden'}}>
        <svg viewBox='0 0 100 104' width='100%' height='100%' role='img' aria-label='Map of India showing orders by state'>
          {/* simplified India silhouette */}
          <path d='M45 8 L55 9 57 15 65 14 63 22 71 25 77 32 72 38 77 46 68 47 64 57 57 63 53 76 48 92 44 79 43 66 35 60 29 53 26 43 31 37 27 29 35 27 35 17 40 12 Z'
            fill='#cfe0f6' stroke='#a9c6ee' strokeWidth='1' strokeLinejoin='round' />
          {/* order-volume bubbles, sized by state volume */}
          <circle cx='50' cy='34' r='9' fill='#2f6fd0' opacity='0.9' />
          <circle cx='44' cy='52' r='7' fill='#4f86dc' opacity='0.85' />
          <circle cx='49' cy='27' r='5.5' fill='#6b9be0' opacity='0.85' />
          <circle cx='50' cy='70' r='5' fill='#9cc0ef' opacity='0.9' />
        </svg>
        <Typography sx={{position: 'absolute', top: 5, left: 0, right: 0, textAlign: 'center', fontFamily: FONT, fontSize: '8.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#3a5b86'}}>
          India
        </Typography>
      </Box>
      <Typography sx={{fontFamily: FONT, fontSize: '9px', color: SUBTLE, textAlign: 'center', mt: 0.6, lineHeight: 1.3}}>
        Each dot is a state · bigger = more orders
      </Typography>
    </Box>
  )
}
